
import { useQuery } from "@tanstack/react-query";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { fetchUnifiedFlights } from "@/services/flights/unifiedFlightQuery";
import { FIDSHeader } from "@/components/fids/FIDSHeader";
import { FIDSFlightBoard } from "@/components/fids/FIDSFlightBoard";
import { FIDSMarquee } from "@/components/fids/FIDSMarquee";
import { FlightDetailPanel } from "@/components/fids/FlightDetailPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OrganizationLinks } from "@/components/OrganizationLinks";
import { useMemo, useState, useCallback, useEffect } from "react";
import { Flight } from "@/types/flight";

function getPNGTodayWindow() {
  const now = new Date();
  const pngOffset = 10 * 60;
  const startOfTodayPNG = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - pngOffset * 60000
  ).toISOString();
  const endOfTodayPNG = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 2) - pngOffset * 60000
  ).toISOString();
  return { startOfTodayPNG, endOfTodayPNG };
}

export default function AirportPage() {
  const { airportCode } = useParams<{ airportCode: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFlight, setSelectedFlight] = useState<Flight | null>(null);
  const code = airportCode?.toUpperCase() || "";

  const { data: flights = [], isLoading } = useQuery({
    queryKey: ['airport-flights', code],
    queryFn: async () => {
      const { startOfTodayPNG, endOfTodayPNG } = getPNGTodayWindow();
      return fetchUnifiedFlights({
        gteDate: startOfTodayPNG,
        ltDate: endOfTodayPNG,
      });
    },
    enabled: !!code,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const airportFlights = useMemo(() =>
    flights.filter(f =>
      f.departure_city?.code === code || f.arrival_city?.code === code
    ),
    [flights, code]
  );

  const airportName = useMemo(() => {
    if (airportFlights.length === 0) return code;
    return airportFlights.find(f => f.departure_city?.code === code)?.departure_city?.name ||
      airportFlights.find(f => f.arrival_city?.code === code)?.arrival_city?.name ||
      code;
  }, [airportFlights, code]);

  const todaysDepartures = useMemo(() =>
    airportFlights.filter(f => f.departure_city?.code === code),
    [airportFlights, code]
  );

  const todaysArrivals = useMemo(() =>
    airportFlights.filter(f => f.arrival_city?.code === code),
    [airportFlights, code]
  );

  useEffect(() => {
    const flightParam = searchParams.get("flight");
    if (flightParam && airportFlights.length > 0 && !selectedFlight) {
      const match = airportFlights.find(f => f.flight_number === flightParam);
      if (match) setSelectedFlight(match);
    }
  }, [searchParams, airportFlights, selectedFlight]);

  const handleFlightClick = useCallback((flight: Flight) => {
    setSelectedFlight(flight);
    setSearchParams({ flight: flight.flight_number }, { replace: true });
  }, [setSearchParams]);

  const handleClosePanel = useCallback(() => {
    setSelectedFlight(null);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#0a0f2c" }}>
      <FIDSHeader title={`${airportName} (${code})`} />

      <div className="w-full px-2 py-2">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-4 transition-colors px-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to All Flights
        </Link>

        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: "#00c8d7" }} />
            <span className="ml-2 text-lg text-white">Loading flights...</span>
          </div>
        ) : (
          <div style={{ backgroundColor: "#0a0f2c" }}>
            <Tabs defaultValue="departures" className="w-full">
              <div style={{ backgroundColor: "#0d1b4b" }}>
                <TabsList className="grid w-full grid-cols-2 h-12 bg-transparent rounded-none">
                  <TabsTrigger
                    value="departures"
                    className="text-base text-white/70 font-bold data-[state=active]:text-white data-[state=active]:bg-[#00c8d7] rounded-none"
                  >
                    Departures ({todaysDepartures.length})
                  </TabsTrigger>
                  <TabsTrigger
                    value="arrivals"
                    className="text-base text-white/70 font-bold data-[state=active]:text-white data-[state=active]:bg-[#00c8d7] rounded-none"
                  >
                    Arrivals ({todaysArrivals.length})
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="departures" className="mt-0">
                <FIDSFlightBoard flights={todaysDepartures} mode="departures" onFlightClick={handleFlightClick} />
              </TabsContent>

              <TabsContent value="arrivals" className="mt-0">
                <FIDSFlightBoard flights={todaysArrivals} mode="arrivals" onFlightClick={handleFlightClick} />
              </TabsContent>
            </Tabs>
          </div>
        )}

        <div className="px-4 py-6 text-center text-white/70 [&_a]:text-[#00c8d7] [&_a:hover]:text-[#00c8d7]/80 [&_p]:text-white/70 [&_.border-t]:border-white/20">
          <OrganizationLinks />
        </div>
      </div>

      <FIDSMarquee />
      <FlightDetailPanel flight={selectedFlight} onClose={handleClosePanel} />
    </div>
  );
}
